import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function CupcakeInquiryForm({ product }) {
    const [form, setForm] = useState({
        event_date: "",
        name: "",
        phone: "",
        message: "",
        flavour_choice: "",
    });
    const [qty, setQty] = useState("");
    const [fulfillment, setFulfillment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!qty) { toast.error("Please choose a quantity (6 or 12)."); return; }
        if (!fulfillment) { toast.error("Please choose pick up or delivery."); return; }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                product_slug: product?.slug,
                product_name: product?.name,
                qty_choice: qty,
                fulfillment_type: fulfillment,
            });
            toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ event_date: "", name: "", phone: "", message: "", flavour_choice: "" });
            setQty("");
            setFulfillment("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="cupcake-inquiry-form">
            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Qty</label>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={qty === "6"}
                            onChange={() => setQty(qty === "6" ? "" : "6")}
                            data-testid="cupcake-qty-6"
                        />
                        <span className="text-sm text-ink">6</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={qty === "12"}
                            onChange={() => setQty(qty === "12" ? "" : "12")}
                            data-testid="cupcake-qty-12"
                        />
                        <span className="text-sm text-ink">12</span>
                    </label>
                </div>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Flavour</label>
                <select
                    name="flavour_choice"
                    required
                    value={form.flavour_choice}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="cupcake-flavour"
                >
                    <option value="">Select a flavour…</option>
                    {(product?.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Message</label>
                <textarea
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink resize-none"
                    data-testid="cupcake-message"
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Date</label>
                    <input
                        type="date"
                        name="event_date"
                        required
                        value={form.event_date}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="cupcake-date"
                    />
                </div>
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name</label>
                    <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="cupcake-name"
                    />
                </div>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Contact</label>
                <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="cupcake-phone"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Pick up or delivery</label>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Pickup"}
                            onChange={() => setFulfillment(fulfillment === "Pickup" ? "" : "Pickup")}
                            data-testid="cupcake-pickup"
                        />
                        <span className="text-sm text-ink">Pick up</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Delivery"}
                            onChange={() => setFulfillment(fulfillment === "Delivery" ? "" : "Delivery")}
                            data-testid="cupcake-delivery"
                        />
                        <span className="text-sm text-ink">Delivery</span>
                    </label>
                </div>
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="cupcake-inquiry-submit"
            >
                {submitting ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
}
